<template>
  <div class="etiquetas-page">
    <div class="etiquetas-header">
      <div>
        <div class="d-flex align-items-center mb-1">
          <span class="page-icon"><i class="fas fa-tags"></i></span>
          <span class="page-eyebrow">Ferramenta do suporte</span>
        </div>
        <h2>Modelos de etiquetas</h2>
        <p>Crie e ajuste modelos visuais sem alterar código.</p>
      </div>
      <button
        type="button"
        class="btn btn-primary font-weight-bold"
        @click="openCreateModal()"
      >
        <i class="fas fa-plus mr-2"></i>Nova etiqueta
      </button>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner spinner-primary spinner-lg"></div>
      <span>Carregando modelos com segurança...</span>
    </div>

    <template v-else>
      <div class="catalog-card">
        <div class="catalog-toolbar">
          <div class="catalog-tabs" role="tablist" aria-label="Filtrar modelos">
            <button
              type="button"
              :class="{ active: tab === 'todos' }"
              @click="tab = 'todos'"
            >
              Todos
            </button>
            <button
              type="button"
              :class="{ active: tab === 'editor' }"
              @click="tab = 'editor'"
            >
              Criados no editor
            </button>
            <button
              type="button"
              :class="{ active: tab === 'legados' }"
              @click="tab = 'legados'"
            >
              Legados
            </button>
            <button
              type="button"
              :class="{ active: tab === 'rascunhos' }"
              @click="tab = 'rascunhos'"
            >
              Rascunhos
            </button>
          </div>
          <div class="catalog-search input-icon">
            <input
              v-model.trim="search"
              type="search"
              class="form-control"
              placeholder="Buscar por nome ou código"
            />
            <span><i class="flaticon2-search-1 text-muted"></i></span>
          </div>
        </div>

        <div v-if="showNewSection" class="catalog-section">
          <div class="section-heading">
            <div>
              <h5>Modelos do editor</h5>
              <p>
                Modelos novos e versões já convertidas para o editor visual.
              </p>
            </div>
            <span
              >{{ filteredNew.length }} encontrado{{
                filteredNew.length === 1 ? "" : "s"
              }}</span
            >
          </div>

          <div v-if="filteredNew.length" class="new-model-grid">
            <article
              v-for="model in filteredNew"
              :key="model.uuid"
              class="model-card"
            >
              <div class="model-card-top">
                <span
                  :class="[
                    'model-symbol',
                    model.tem_publicacao ? 'published' : 'draft',
                  ]"
                >
                  <i
                    :class="
                      model.tem_publicacao ? 'fas fa-tag' : 'fas fa-pencil-alt'
                    "
                  ></i>
                </span>
                <span
                  :class="[
                    'state-pill',
                    model.tem_publicacao ? 'published' : 'draft',
                  ]"
                >
                  <i></i>{{ model.tem_publicacao ? "Publicado" : "Rascunho" }}
                </span>
                <b-dropdown
                  right
                  no-caret
                  size="sm"
                  variant="link"
                  toggle-class="model-menu"
                  menu-class="shadow-sm"
                >
                  <template #button-content
                    ><i class="fas fa-ellipsis-v"></i
                  ></template>
                  <b-dropdown-item @click="duplicateModel(model)"
                    ><i class="far fa-copy mr-2"></i>Duplicar</b-dropdown-item
                  >
                  <b-dropdown-item
                    v-if="model.tem_rascunho"
                    class="text-danger"
                    @click="discardDraft(model)"
                    ><i class="fas fa-undo-alt mr-2"></i
                    >{{ discardDraftLabel(model) }}</b-dropdown-item
                  >
                  <b-dropdown-divider></b-dropdown-divider>
                  <b-dropdown-item
                    class="text-danger"
                    @click="archiveModel(model)"
                    ><i class="far fa-trash-alt mr-2"></i
                    >Arquivar</b-dropdown-item
                  >
                </b-dropdown>
              </div>
              <h6 :title="model.nome">{{ model.nome }}</h6>
              <p>{{ model.descricao || "Modelo visual de etiqueta." }}</p>
              <div class="model-meta">
                <span
                  ><i class="fas fa-building"></i>{{ scopeLabel(model) }}</span
                >
                <span
                  ><i class="fas fa-history"></i>{{ versionLabel(model) }}</span
                >
              </div>
              <button
                type="button"
                class="btn btn-sm btn-light-primary btn-block font-weight-bold"
                @click="editModel(model)"
              >
                {{
                  model.tem_rascunho ? "Continuar edição" : "Criar nova versão"
                }}
                <i class="fas fa-arrow-right ml-2"></i>
              </button>
              <button
                v-if="model.tem_rascunho"
                type="button"
                class="draft-discard-action btn btn-sm btn-light-danger btn-block font-weight-bold mt-2"
                :class="{
                  'spinner spinner-danger spinner-right':
                    discardingUuid === model.uuid,
                }"
                :disabled="discardingUuid === model.uuid"
                @click="discardDraft(model)"
              >
                <i class="fas fa-undo-alt mr-2"></i
                >{{ discardDraftLabel(model) }}
              </button>
            </article>
          </div>

          <div v-else class="section-empty">
            <span><i class="fas fa-pencil-ruler"></i></span>
            <div>
              <strong>Nenhum modelo do editor aqui</strong
              ><small>{{
                search
                  ? "Tente outra busca."
                  : "Comece criando uma nova etiqueta visual."
              }}</small>
            </div>
            <button
              v-if="!search"
              type="button"
              class="btn btn-sm btn-primary font-weight-bold"
              @click="openCreateModal()"
            >
              Criar primeira etiqueta
            </button>
          </div>
        </div>

        <div v-if="showLegacySection" class="catalog-section legacy-section">
          <div class="section-heading">
            <div>
              <h5>Catálogo legado</h5>
              <p>
                Estes modelos permanecem no motor original até uma publicação
                explícita.
              </p>
            </div>
            <span
              >{{ filteredLegacy.length }} encontrado{{
                filteredLegacy.length === 1 ? "" : "s"
              }}</span
            >
          </div>

          <div v-if="filteredLegacy.length" class="legacy-list">
            <article
              v-for="row in filteredLegacy"
              :key="row.codigo_legado"
              class="legacy-row"
            >
              <div class="legacy-symbol"><i class="fas fa-shield-alt"></i></div>
              <div class="legacy-main">
                <div class="d-flex align-items-center flex-wrap">
                  <h6>{{ row.nome }}</h6>
                  <span class="legacy-code">{{ row.codigo_legado }}</span>
                  <span :class="['state-pill ml-2', stateClass(row.estado)]"
                    ><i></i>{{ stateLabel(row.estado) }}</span
                  >
                </div>
                <p>{{ row.descricao }}</p>
                <div
                  v-if="row.variantes && row.variantes.length"
                  class="legacy-variants"
                >
                  <div
                    v-for="variant in row.variantes"
                    :key="variant.uuid"
                    class="legacy-variant"
                  >
                    <button type="button" @click="editModel(variant)">
                      <i
                        :class="
                          variant.tem_publicacao
                            ? 'fas fa-check-circle'
                            : 'far fa-save'
                        "
                      ></i>
                      <span>{{ scopeLabel(variant) }}</span>
                      <small>{{
                        variant.tem_publicacao
                          ? `v${variant.versao_publicada} em uso`
                          : `rascunho v${variant.versao_rascunho}`
                      }}</small>
                    </button>
                    <button
                      v-if="variant.tem_rascunho"
                      type="button"
                      class="discard-variant"
                      :title="discardDraftLabel(variant)"
                      :aria-label="discardDraftLabel(variant)"
                      :disabled="discardingUuid === variant.uuid"
                      @click="discardDraft(variant)"
                    >
                      <i class="fas fa-undo-alt mr-1"></i>Cancelar rascunho
                    </button>
                  </div>
                </div>
              </div>
              <div class="legacy-actions">
                <button
                  v-if="row.modelo"
                  type="button"
                  class="btn btn-sm btn-primary font-weight-bold"
                  @click="editModel(row.modelo)"
                >
                  Abrir editor
                </button>
                <button
                  type="button"
                  :class="
                    row.modelo
                      ? 'btn btn-sm btn-light-primary font-weight-bold mt-2'
                      : 'btn btn-sm btn-outline-primary font-weight-bold'
                  "
                  @click="openLegacyModal(row)"
                >
                  {{ row.modelo ? "Outro escopo" : "Criar versão editável" }}
                </button>
                <button
                  v-if="row.modelo && row.modelo.tem_rascunho"
                  type="button"
                  class="btn btn-sm btn-light-danger font-weight-bold mt-2"
                  :disabled="discardingUuid === row.modelo.uuid"
                  @click="discardDraft(row.modelo)"
                >
                  {{ discardDraftLabel(row.modelo) }}
                </button>
              </div>
            </article>
          </div>

          <div v-else class="section-empty">
            <span><i class="fas fa-search"></i></span>
            <div>
              <strong>Nenhum legado encontrado</strong
              ><small>Altere os termos da busca.</small>
            </div>
          </div>
        </div>
      </div>
    </template>

    <b-modal
      id="etiqueta-create-modal"
      size="lg"
      centered
      hide-footer
      no-close-on-backdrop
      @hidden="resetModal"
    >
      <template #modal-title>
        <div class="modal-title-with-icon">
          <span
            ><i
              :class="
                modalMode === 'legacy' ? 'fas fa-shield-alt' : 'fas fa-plus'
              "
            ></i
          ></span>
          <div>
            <strong>{{
              modalMode === "legacy" ? "Criar versão editável" : "Nova etiqueta"
            }}</strong>
            <small>{{
              modalMode === "legacy"
                ? selectedLegacyName
                : "Comece por um formato pronto"
            }}</small>
          </div>
        </div>
      </template>

      <div v-if="modalMode === 'legacy'" class="modal-safety-note">
        <i class="fas fa-lock"></i>
        <div>
          <strong>O modelo original seguirá em uso</strong
          ><span
            >Esta ação cria somente um rascunho editável. Nada muda até a
            publicação.</span
          >
        </div>
      </div>

      <template v-else>
        <div class="row">
          <div class="col-md-7 form-group">
            <label>Nome do modelo <span class="text-danger">*</span></label>
            <input
              v-model.trim="form.name"
              type="text"
              maxlength="120"
              class="form-control"
              :class="{ 'is-invalid': submitted && !form.name }"
              placeholder="Ex.: Preço promocional 50 × 30"
              autofocus
            />
            <div class="invalid-feedback">
              Informe um nome para identificar o modelo.
            </div>
          </div>
          <div class="col-md-5 form-group">
            <label
              >Descrição
              <span class="text-muted font-weight-normal"
                >(opcional)</span
              ></label
            >
            <input
              v-model.trim="form.description"
              type="text"
              maxlength="255"
              class="form-control"
              placeholder="Onde e quando é usada"
            />
          </div>
        </div>

        <label class="d-block mb-2">Formato inicial</label>
        <div class="preset-grid mb-5">
          <button
            v-for="preset in presets"
            :key="preset.value"
            type="button"
            :class="['preset-card', { selected: form.preset === preset.value }]"
            @click="form.preset = preset.value"
          >
            <span class="preset-radio"><i></i></span>
            <span
              ><strong>{{ preset.label }}</strong
              ><small>{{ preset.description }}</small></span
            >
          </button>
        </div>
      </template>

      <div class="scope-area">
        <label class="d-block mb-2">Onde este modelo ficará disponível?</label>
        <div class="scope-grid">
          <button
            type="button"
            :class="['scope-card', { selected: form.scope === 'global' }]"
            @click="selectScope('global')"
          >
            <span class="scope-icon"
              ><i class="fas fa-globe-americas"></i
            ></span>
            <span
              ><strong>Todas as empresas</strong
              ><small>Disponível globalmente após publicar</small></span
            >
            <i class="fas fa-check-circle scope-check"></i>
          </button>
          <button
            type="button"
            :class="['scope-card', { selected: form.scope === 'empresa' }]"
            @click="selectScope('empresa')"
          >
            <span class="scope-icon"><i class="fas fa-building"></i></span>
            <span
              ><strong>Uma empresa</strong
              ><small>Modelo exclusivo para um cliente</small></span
            >
            <i class="fas fa-check-circle scope-check"></i>
          </button>
        </div>

        <div v-if="form.scope === 'empresa'" class="company-picker">
          <label>Empresa que receberá o modelo</label>
          <div v-if="selectedCompany" class="selected-company">
            <span><i class="fas fa-building"></i></span>
            <div>
              <strong>{{ companyName(selectedCompany) }}</strong
              ><small>{{
                selectedCompany.cnpj_cpf ||
                `Código ${selectedCompany.id_empresa}`
              }}</small>
            </div>
            <button
              type="button"
              class="btn btn-xs btn-icon btn-light"
              @click="clearCompany"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
          <template v-else>
            <div class="input-icon">
              <input
                v-model.trim="companySearch"
                class="form-control"
                placeholder="Busque por nome ou CNPJ"
                @input="scheduleCompanySearch"
              />
              <span
                ><i
                  :class="
                    searchingCompanies
                      ? 'spinner spinner-sm spinner-primary'
                      : 'flaticon2-search-1 text-muted'
                  "
                ></i
              ></span>
            </div>
            <div v-if="companies.length" class="company-results">
              <button
                v-for="company in companies"
                :key="company.id_empresa"
                type="button"
                @click="chooseCompany(company)"
              >
                <span
                  ><strong>{{ companyName(company) }}</strong
                  ><small>{{
                    company.cnpj_cpf || `Código ${company.id_empresa}`
                  }}</small></span
                >
                <i class="fas fa-chevron-right"></i>
              </button>
            </div>
            <small
              v-else-if="companySearch && !searchingCompanies"
              class="text-muted d-block mt-2"
              >Nenhuma empresa encontrada.</small
            >
          </template>
          <div
            v-if="submitted && !form.companyId"
            class="text-danger font-size-xs mt-2"
          >
            Selecione a empresa.
          </div>
        </div>
      </div>

      <div class="modal-actions">
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          :disabled="saving"
          @click="$bvModal.hide('etiqueta-create-modal')"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :class="{ 'spinner spinner-light spinner-right': saving }"
          :disabled="saving"
          @click="submitModal"
        >
          {{
            modalMode === "legacy"
              ? "Criar rascunho e editar"
              : "Criar e abrir editor"
          }}
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import etiquetasService from "@/modules/Etiquetas/services/etiquetasService";
import { scopeLabel } from "@/modules/Etiquetas/services/etiquetaDefinition";

const emptyForm = () => ({
  name: "",
  description: "",
  preset: "termica_50x30",
  scope: "global",
  companyId: null,
});

export default {
  name: "EtiquetasIndex",
  data() {
    return {
      loading: true,
      saving: false,
      submitted: false,
      search: "",
      tab: "todos",
      catalog: { legados: [], novos: [], totais: {} },
      presets: [],
      modalMode: "new",
      selectedLegacy: null,
      form: emptyForm(),
      companySearch: "",
      companies: [],
      selectedCompany: null,
      searchingCompanies: false,
      companyTimer: null,
      discardingUuid: null,
    };
  },
  computed: {
    normalizedSearch() {
      return this.normalize(this.search);
    },
    filteredNew() {
      return (this.catalog.novos || []).filter((model) => {
        const match =
          !this.normalizedSearch ||
          this.normalize(
            `${model.nome} ${model.descricao} ${model.codigo_seletor}`
          ).includes(this.normalizedSearch);
        const tabMatch = this.tab !== "rascunhos" || model.tem_rascunho;
        return match && tabMatch;
      });
    },
    filteredLegacy() {
      return (this.catalog.legados || []).filter((row) => {
        const match =
          !this.normalizedSearch ||
          this.normalize(`${row.nome} ${row.codigo_legado}`).includes(
            this.normalizedSearch
          );
        const hasDraft =
          row.estado === "rascunho" ||
          Boolean(row.modelo && row.modelo.tem_rascunho) ||
          (row.variantes || []).some((variant) => variant.tem_rascunho);
        const tabMatch = this.tab !== "rascunhos" || hasDraft;
        return match && tabMatch;
      });
    },
    showNewSection() {
      return ["todos", "editor", "rascunhos"].includes(this.tab);
    },
    showLegacySection() {
      return ["todos", "legados", "rascunhos"].includes(this.tab);
    },
    selectedLegacyName() {
      return this.selectedLegacy
        ? `${this.selectedLegacy.nome} · ${this.selectedLegacy.codigo_legado}`
        : "Modelo legado";
    },
  },
  async mounted() {
    await this.load();
    if (this.$route.query.novo === "1") {
      this.openCreateModal();
      this.$router.replace({ name: "etiquetasLista" }).catch(() => {});
    }
  },
  beforeDestroy() {
    if (this.companyTimer) clearTimeout(this.companyTimer);
  },
  methods: {
    scopeLabel,
    async load() {
      this.loading = true;
      try {
        const [bootstrap, models] = await Promise.all([
          etiquetasService.inicializacao(),
          etiquetasService.modelos(),
        ]);
        this.presets = bootstrap.data.presets || [];
        this.catalog = models.data || { legados: [], novos: [], totais: {} };
      } catch (error) {
        this.catalog = { legados: [], novos: [], totais: {} };
      } finally {
        this.loading = false;
      }
    },
    normalize(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    },
    stateLabel(state) {
      return (
        {
          legado_protegido: "Legado protegido",
          rascunho: "Rascunho do editor",
          publicado_editor: "Editor em uso",
        }[state] || state
      );
    },
    stateClass(state) {
      return state === "publicado_editor"
        ? "published"
        : state === "rascunho"
        ? "draft"
        : "legacy";
    },
    versionLabel(model) {
      if (model.tem_rascunho) return `Rascunho v${model.versao_rascunho}`;
      if (model.tem_publicacao) return `Versão ${model.versao_publicada}`;
      return "Ainda não publicado";
    },
    editModel(model) {
      this.$router.push({
        name: "etiquetasEditar",
        params: { uuid: model.uuid },
      });
    },
    openCreateModal() {
      this.resetModal();
      this.modalMode = "new";
      this.$bvModal.show("etiqueta-create-modal");
    },
    openLegacyModal(row) {
      this.resetModal();
      this.modalMode = "legacy";
      this.selectedLegacy = row;
      this.$bvModal.show("etiqueta-create-modal");
    },
    resetModal() {
      this.submitted = false;
      this.saving = false;
      this.form = emptyForm();
      this.selectedLegacy = null;
      this.companySearch = "";
      this.companies = [];
      this.selectedCompany = null;
    },
    selectScope(scope) {
      this.form.scope = scope;
      if (scope === "global") this.clearCompany();
      else this.searchCompanies();
    },
    scheduleCompanySearch() {
      if (this.companyTimer) clearTimeout(this.companyTimer);
      this.companyTimer = setTimeout(this.searchCompanies, 280);
    },
    async searchCompanies() {
      this.searchingCompanies = true;
      try {
        const { data } = await etiquetasService.buscarEmpresas(
          this.companySearch
        );
        this.companies = Array.isArray(data) ? data : [];
      } catch (error) {
        this.companies = [];
      } finally {
        this.searchingCompanies = false;
      }
    },
    chooseCompany(company) {
      this.selectedCompany = company;
      this.form.companyId = company.id_empresa;
      this.companies = [];
    },
    clearCompany() {
      this.selectedCompany = null;
      this.form.companyId = null;
      this.companySearch = "";
      this.companies = [];
    },
    companyName(company) {
      return (
        company.nome_fantasia || company.nome || `Empresa ${company.id_empresa}`
      );
    },
    async submitModal() {
      this.submitted = true;
      const invalidName = this.modalMode === "new" && !this.form.name;
      const invalidCompany =
        this.form.scope === "empresa" && !this.form.companyId;
      if (invalidName || invalidCompany) return;

      this.saving = true;
      try {
        const scope = {
          escopo: this.form.scope,
          id_empresa_destino:
            this.form.scope === "empresa" ? this.form.companyId : null,
        };
        const response =
          this.modalMode === "legacy"
            ? await etiquetasService.iniciarLegado({
                codigo_legado: this.selectedLegacy.codigo_legado,
                ...scope,
              })
            : await etiquetasService.criar({
                nome: this.form.name,
                descricao: this.form.description || null,
                preset: this.form.preset,
                ...scope,
              });
        this.$bvModal.hide("etiqueta-create-modal");
        this.$toast.success(
          this.modalMode === "legacy"
            ? "Rascunho criado. A etiqueta original continua ativa."
            : "Modelo criado como rascunho."
        );
        this.editModel(response.data);
      } catch (error) {
        this.saving = false;
      }
    },
    async duplicateModel(model) {
      const result = await this.$swal.fire({
        title: "Duplicar modelo",
        text: "A cópia será criada como rascunho e não afetará a impressão.",
        input: "text",
        inputValue: `${model.nome} - Cópia`,
        inputLabel: "Nome da cópia",
        showCancelButton: true,
        confirmButtonText: "Criar cópia",
        cancelButtonText: "Cancelar",
        inputValidator: (value) =>
          !String(value || "").trim() ? "Informe um nome." : undefined,
      });
      if (!result.isConfirmed) return;
      try {
        const { data } = await etiquetasService.duplicar({
          modelo_uuid: model.uuid,
          nome: result.value,
        });
        this.$toast.success("Cópia criada como rascunho.");
        this.editModel(data);
      } catch (error) {
        // O interceptor apresenta a mensagem da API.
      }
    },
    async archiveModel(model) {
      const result = await this.$swal.fire({
        icon: "warning",
        title: "Arquivar este modelo?",
        html: `<p>O modelo <strong>${this.escapeHtml(
          model.nome
        )}</strong> deixará de aparecer para impressão.</p><p class="text-muted font-size-sm">Esta ação não apaga as etiquetas legadas.</p>`,
        showCancelButton: true,
        confirmButtonText: "Sim, arquivar",
        confirmButtonColor: "#f64e60",
        cancelButtonText: "Cancelar",
      });
      if (!result.isConfirmed) return;
      try {
        await etiquetasService.arquivar(model.uuid, model.token_revisao);
        this.$toast.success("Modelo arquivado.");
        await this.load();
      } catch (error) {
        // O interceptor apresenta a mensagem da API.
      }
    },
    discardDraftLabel(model) {
      return model.origem === "legado" && !model.tem_publicacao
        ? "Cancelar alterações e usar legado"
        : "Excluir rascunho";
    },
    async discardDraft(model) {
      if (this.discardingUuid) return;
      const returnsToLegacy =
        model.origem === "legado" && !model.tem_publicacao;
      const removesModel = model.origem !== "legado" && !model.tem_publicacao;
      const result = await this.$swal.fire({
        icon: "warning",
        title: returnsToLegacy
          ? "Cancelar alterações e usar o legado?"
          : "Excluir este rascunho?",
        text: returnsToLegacy
          ? "A versão editável será removida e a etiqueta legada continuará em uso."
          : removesModel
          ? "Este modelo ainda não foi publicado e será removido da lista."
          : "As alterações em andamento serão removidas. A versão publicada continuará em uso.",
        showCancelButton: true,
        confirmButtonText: this.discardDraftLabel(model),
        confirmButtonColor: "#f64e60",
        cancelButtonText: "Voltar",
      });
      if (!result.isConfirmed) return;

      this.discardingUuid = model.uuid;
      try {
        await etiquetasService.descartarRascunho(
          model.uuid,
          model.token_revisao
        );
        this.$toast.success(
          returnsToLegacy
            ? "Alterações canceladas. A etiqueta legada segue em uso."
            : "Rascunho excluído."
        );
        await this.load();
      } catch (error) {
        // O interceptor apresenta a mensagem da API.
      } finally {
        this.discardingUuid = null;
      }
    },
    escapeHtml(value) {
      const div = document.createElement("div");
      div.textContent = String(value || "");
      return div.innerHTML;
    },
  },
};
</script>

<style lang="scss" scoped>
.etiquetas-page {
  padding-bottom: 30px;
}

.etiquetas-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 22px;

  h2 {
    margin: 0;
    color: #181c32;
    font-size: 24px;
    font-weight: 800;
  }

  p {
    margin: 5px 0 0;
    color: #7e8299;
  }
}

.page-icon {
  width: 25px;
  height: 25px;
  margin-right: 8px;
  border-radius: 7px;
  background: #f0ebff;
  color: #8950fc;
  display: grid;
  place-items: center;
  font-size: 11px;
}

.page-eyebrow {
  color: #8950fc;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.loading-state {
  min-height: 320px;
  border-radius: 12px;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  color: #7e8299;
}

.catalog-card {
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(31, 45, 61, 0.04);
  overflow: hidden;
}

.catalog-toolbar {
  min-height: 72px;
  padding: 14px 20px;
  border-bottom: 1px solid #eef0f5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.catalog-tabs {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  border-radius: 9px;
  background: #f3f6f9;

  button {
    min-height: 31px;
    padding: 0 12px;
    border: 0;
    border-radius: 7px;
    background: transparent;
    color: #7e8299;
    font-size: 10px;
    font-weight: 700;

    &.active {
      background: #fff;
      color: #8950fc;
      box-shadow: 0 2px 6px rgba(31, 45, 61, 0.08);
    }
  }
}

.catalog-search {
  width: 280px;
}

.catalog-section {
  padding: 22px;
}

.legacy-section {
  border-top: 8px solid #f3f6f9;
}

.section-heading {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 17px;

  h5 {
    margin: 0;
    color: #181c32;
    font-weight: 700;
  }

  p {
    margin: 4px 0 0;
    color: #7e8299;
    font-size: 11px;
  }

  > span {
    color: #a1a5b7;
    font-size: 10px;
  }
}

.new-model-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.model-card {
  min-width: 0;
  padding: 16px;
  border: 1px solid #e9ebf2;
  border-radius: 10px;
  transition: 0.18s ease;

  &:hover {
    border-color: #d3c4f5;
    box-shadow: 0 7px 18px rgba(53, 41, 85, 0.08);
    transform: translateY(-1px);
  }

  h6 {
    margin: 13px 0 4px;
    color: #181c32;
    font-size: 13px;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  > p {
    height: 32px;
    margin: 0 0 12px;
    color: #7e8299;
    font-size: 10px;
    line-height: 1.5;
    overflow: hidden;
  }
}

.model-card-top {
  display: flex;
  align-items: center;
}

.model-symbol {
  width: 36px;
  height: 36px;
  margin-right: 8px;
  border-radius: 9px;
  display: grid;
  place-items: center;

  &.published {
    background: #e8fff3;
    color: #1bc5bd;
  }
  &.draft {
    background: #fff4de;
    color: #ffa800;
  }
}

.state-pill {
  padding: 4px 7px;
  border-radius: 20px;
  font-size: 9px;
  font-weight: 700;
  white-space: nowrap;

  i {
    width: 5px;
    height: 5px;
    margin-right: 4px;
    border-radius: 50%;
    display: inline-block;
    background: currentColor;
  }

  &.published {
    background: #e8fff3;
    color: #169f6c;
  }
  &.draft {
    background: #fff4de;
    color: #b76e00;
  }
  &.legacy {
    background: #f3f6f9;
    color: #7e8299;
  }
}

::v-deep .model-menu {
  margin-left: auto;
  padding: 5px 9px !important;
  color: #a1a5b7 !important;
}

.model-meta {
  min-height: 48px;
  padding: 9px 0;
  border-top: 1px solid #eef0f5;
  color: #7e8299;
  font-size: 9px;

  span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span + span {
    margin-top: 4px;
  }

  i {
    width: 16px;
    color: #b5b5c3;
  }
}

.legacy-list {
  border: 1px solid #e9ebf2;
  border-radius: 10px;
  overflow: hidden;
}

.legacy-row {
  min-height: 93px;
  padding: 14px 16px;
  border-bottom: 1px solid #eef0f5;
  display: flex;
  align-items: flex-start;
  gap: 12px;

  &:last-child {
    border-bottom: 0;
  }
}

.legacy-symbol {
  width: 35px;
  height: 35px;
  flex: 0 0 35px;
  border-radius: 9px;
  background: #f3f6f9;
  color: #7e8299;
  display: grid;
  place-items: center;
}

.legacy-main {
  min-width: 0;
  flex: 1;

  h6 {
    margin: 0 8px 0 0;
    color: #3f4254;
    font-size: 12px;
    font-weight: 700;
  }

  > p {
    margin: 4px 0 0;
    color: #7e8299;
    font-size: 10px;
  }
}

.legacy-code {
  padding: 2px 5px;
  border-radius: 4px;
  background: #f3f6f9;
  color: #7e8299;
  font: 500 9px/1.4 "Courier New", monospace;
}

.legacy-variants {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 9px;
}

.legacy-variant {
  display: flex;

  > button {
    padding: 5px 8px;
    border: 1px solid #e4e6ef;
    background: #fff;
    color: #5e6278;
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 9px;

    &:first-child {
      border-radius: 6px 0 0 6px;
    }

    &:hover:not(:disabled) {
      border-color: #8950fc;
      color: #8950fc;
    }

    i {
      color: #1bc5bd;
    }
    small {
      color: #a1a5b7;
    }
  }

  > .discard-variant {
    padding: 5px 9px;
    border-left: 0;
    border-radius: 0 6px 6px 0;
    color: #f64e60;
    white-space: nowrap;
    font-size: 9px;
    font-weight: 600;

    &:hover:not(:disabled) {
      border-color: #f64e60;
      background: #fff5f6;
      color: #d9364b;
    }
  }
}

.legacy-actions {
  width: 150px;
  flex: 0 0 150px;
  display: flex;
  flex-direction: column;
}

.section-empty {
  min-height: 105px;
  border: 1px dashed #d8dbe5;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  > span {
    width: 38px;
    height: 38px;
    border-radius: 9px;
    background: #f3f6f9;
    color: #a1a5b7;
    display: grid;
    place-items: center;
  }

  strong,
  small {
    display: block;
  }
  strong {
    color: #5e6278;
    font-size: 11px;
  }
  small {
    color: #a1a5b7;
    font-size: 9px;
    margin-top: 3px;
  }
}

.modal-title-with-icon {
  display: flex;
  align-items: center;
  gap: 10px;

  > span {
    width: 36px;
    height: 36px;
    border-radius: 9px;
    background: #f0ebff;
    color: #8950fc;
    display: grid;
    place-items: center;
  }

  strong,
  small {
    display: block;
  }
  strong {
    color: #181c32;
    font-size: 14px;
  }
  small {
    color: #7e8299;
    font-size: 10px;
    margin-top: 2px;
  }
}

.modal-safety-note {
  margin-bottom: 18px;
  padding: 12px;
  border-radius: 9px;
  background: #e8fff3;
  color: #16875f;
  display: flex;
  align-items: center;
  gap: 10px;

  i {
    font-size: 17px;
  }
  strong,
  span {
    display: block;
  }
  strong {
    font-size: 11px;
  }
  span {
    font-size: 10px;
    margin-top: 2px;
  }
}

.preset-grid,
.scope-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 9px;
}

.preset-card,
.scope-card {
  min-height: 67px;
  padding: 11px;
  border: 1px solid #e4e6ef;
  border-radius: 9px;
  background: #fff;
  color: #5e6278;
  display: flex;
  align-items: center;
  gap: 9px;
  text-align: left;
  transition: 0.15s ease;

  strong,
  small {
    display: block;
  }
  strong {
    color: #3f4254;
    font-size: 10px;
  }
  small {
    color: #a1a5b7;
    font-size: 9px;
    margin-top: 3px;
  }

  &.selected {
    border-color: #8950fc;
    background: #faf8ff;
    box-shadow: inset 0 0 0 1px #8950fc;
  }
}

.preset-radio {
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
  border: 1px solid #d8dbe5;
  border-radius: 50%;
  display: grid;
  place-items: center;

  .selected & {
    border-color: #8950fc;

    i {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #8950fc;
    }
  }
}

.scope-area {
  padding-top: 17px;
  border-top: 1px solid #eef0f5;
}

.scope-grid {
  grid-template-columns: 1fr 1fr;
}

.scope-card {
  position: relative;
  min-height: 72px;
}

.scope-icon {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  border-radius: 8px;
  background: #f3f6f9;
  color: #7e8299;
  display: grid;
  place-items: center;

  .selected & {
    background: #f0ebff;
    color: #8950fc;
  }
}

.scope-check {
  margin-left: auto;
  color: #8950fc;
  opacity: 0;

  .selected & {
    opacity: 1;
  }
}

.company-picker {
  margin-top: 12px;
  padding: 13px;
  border-radius: 9px;
  background: #f7f8fb;
}

.company-results {
  max-height: 170px;
  margin-top: 4px;
  border: 1px solid #e4e6ef;
  border-radius: 7px;
  background: #fff;
  overflow-y: auto;

  button {
    width: 100%;
    padding: 9px 11px;
    border: 0;
    border-bottom: 1px solid #eef0f5;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: space-between;
    text-align: left;

    &:hover {
      background: #f7f8fb;
    }
    &:last-child {
      border-bottom: 0;
    }
    strong,
    small {
      display: block;
    }
    strong {
      color: #3f4254;
      font-size: 10px;
    }
    small {
      color: #a1a5b7;
      font-size: 9px;
      margin-top: 2px;
    }
    > i {
      color: #b5b5c3;
      font-size: 9px;
    }
  }
}

.selected-company {
  min-height: 52px;
  padding: 8px 10px;
  border: 1px solid #cfc1f4;
  border-radius: 8px;
  background: #fff;
  display: flex;
  align-items: center;
  gap: 9px;

  > span {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #f0ebff;
    color: #8950fc;
    display: grid;
    place-items: center;
  }

  > div {
    flex: 1;
  }
  strong,
  small {
    display: block;
  }
  strong {
    color: #3f4254;
    font-size: 10px;
  }
  small {
    color: #a1a5b7;
    font-size: 9px;
  }
}

.modal-actions {
  margin: 20px -1.75rem -1.75rem;
  padding: 14px 1.75rem;
  border-top: 1px solid #eef0f5;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

@media (max-width: 1199.98px) {
  .new-model-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 767.98px) {
  .etiquetas-header {
    align-items: stretch;
    flex-direction: column;
  }
  .new-model-grid {
    grid-template-columns: 1fr;
  }
  .catalog-toolbar {
    align-items: stretch;
    flex-direction: column;
  }
  .catalog-tabs {
    overflow-x: auto;
  }
  .catalog-tabs button {
    flex: 0 0 auto;
  }
  .catalog-search {
    width: 100%;
  }
  .legacy-row {
    flex-wrap: wrap;
  }
  .legacy-actions {
    width: 100%;
    flex: 1 0 100%;
    flex-direction: row;
    gap: 8px;
  }
  .legacy-actions .mt-2 {
    margin-top: 0 !important;
  }
  .preset-grid,
  .scope-grid {
    grid-template-columns: 1fr;
  }
}
</style>
